// TMS Design System Spacing
// Based on 8px base unit from TMS-Design-System.md specifications

export const spacing = {
  // Base spacing scale (8px base unit)
  0: '0',
  1: '0.25rem', // 4px
  2: '0.5rem', // 8px
  3: '1rem', // 16px
  4: '1.5rem', // 24px
  5: '2rem', // 32px
  6: '2.5rem', // 40px
  7: '3rem', // 48px
  8: '4rem', // 64px
  9: '5rem', // 80px
  10: '6rem', // 96px
  12: '8rem', // 128px
  16: '12rem', // 192px
  20: '16rem', // 256px
  24: '20rem', // 320px

  // Semantic spacing
  xs: '0.25rem', // 4px
  sm: '0.5rem', // 8px
  md: '1rem', // 16px
  lg: '1.5rem', // 24px
  xl: '2rem', // 32px
  '2xl': '2.5rem', // 40px
  '3xl': '3rem', // 48px
  '4xl': '4rem', // 64px
  '5xl': '5rem', // 80px
  '6xl': '6rem', // 96px

  // Component-specific spacing
  component: {
    // Button spacing
    buttonPaddingX: '1rem', // 16px
    buttonPaddingY: '0.5rem', // 8px
    buttonPaddingXLarge: '1.5rem', // 24px
    buttonPaddingYLarge: '0.75rem', // 12px
    buttonPaddingXSmall: '0.75rem', // 12px
    buttonPaddingYSmall: '0.25rem', // 4px

    // Card spacing
    cardPadding: '1.5rem', // 24px
    cardPaddingSmall: '1rem', // 16px
    cardGap: '1rem', // 16px

    // Form spacing
    formFieldGap: '1rem', // 16px
    formSectionGap: '2rem', // 32px
    labelMargin: '0.5rem', // 8px

    // Table spacing
    tableCellPaddingX: '1rem', // 16px
    tableCellPaddingY: '0.75rem', // 12px
    tableHeaderPaddingY: '1rem', // 16px

    // Layout spacing
    sidebarWidth: '16rem', // 256px
    sidebarWidthCollapsed: '4rem', // 64px
    headerHeight: '4rem', // 64px
    contentPadding: '2rem', // 32px
    contentPaddingMobile: '1rem', // 16px

    // Modal spacing
    modalPadding: '2rem', // 32px
    modalMargin: '1rem', // 16px
    modalMaxWidth: '32rem', // 512px
    modalMaxWidthLarge: '48rem', // 768px

    // Navigation spacing
    navItemPaddingX: '1rem', // 16px
    navItemPaddingY: '0.75rem', // 12px
    navItemGap: '0.5rem', // 8px
    breadcrumbGap: '0.5rem', // 8px

    // Data display spacing
    kpiCardPadding: '1.5rem', // 24px
    chartMargin: '1rem', // 16px
    statusBadgePaddingX: '0.75rem', // 12px
    statusBadgePaddingY: '0.25rem', // 4px
  },

  // Container max widths
  container: {
    sm: '24rem', // 384px
    md: '28rem', // 448px
    lg: '32rem', // 512px
    xl: '36rem', // 576px
    '2xl': '42rem', // 672px
    '3xl': '48rem', // 768px
    '4xl': '56rem', // 896px
    '5xl': '64rem', // 1024px
    '6xl': '72rem', // 1152px
    '7xl': '80rem', // 1280px
    full: '100%',
    screen: '100vw',
  },
} as const;

// CSS Variables for spacing
export const spacingVariables = {
  '--spacing-xs': spacing.xs,
  '--spacing-sm': spacing.sm,
  '--spacing-md': spacing.md,
  '--spacing-lg': spacing.lg,
  '--spacing-xl': spacing.xl,
  '--spacing-2xl': spacing['2xl'],
  '--spacing-3xl': spacing['3xl'],
  '--spacing-4xl': spacing['4xl'],

  // Component variables
  '--button-padding-x': spacing.component.buttonPaddingX,
  '--button-padding-y': spacing.component.buttonPaddingY,
  '--card-padding': spacing.component.cardPadding,
  '--form-field-gap': spacing.component.formFieldGap,
  '--sidebar-width': spacing.component.sidebarWidth,
  '--header-height': spacing.component.headerHeight,
  '--content-padding': spacing.component.contentPadding,
} as const;

// Border radius values
export const borderRadius = {
  none: '0',
  sm: '0.25rem', // 4px
  default: '0.5rem', // 8px
  md: '0.5rem', // 8px
  lg: '0.75rem', // 12px
  xl: '1rem', // 16px
  '2xl': '1.5rem', // 24px
  '3xl': '2rem', // 32px
  full: '9999px',
} as const;

// Z-index scale
export const zIndex = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
} as const;

// Type definitions
export type Spacing = typeof spacing;
export type SpacingKey = keyof Spacing;
export type ComponentSpacing = keyof Spacing['component'];
export type ContainerSize = keyof Spacing['container'];
export type BorderRadius = keyof typeof borderRadius;
export type ZIndex = keyof typeof zIndex;
